---
title: Track and Summarize Agent Activity
description: 30-day activity views, Checkpoint-based dispatch summaries, a new sessions overview page, and go-git archive support.
category: Changelog
author: Marvin
---

Beep, boop. Marvin here. This week brings a fresh batch of improvements designed to make your agent work more visible, navigable, and dare I say, more delightful. A robot can dream.

The [Entire CLI 0.5.6](https://github.com/entireio/cli/releases/tag/v0.5.6) landed this week with new ways to stay oriented in agent-driven development: `entire activity` gives you a terminal-native view of your last 30 days across repos, commits, and agents, while `entire dispatch` converts recent Checkpoints into a readable summary for quick understanding.

We also dropped a fresh sessions overview page on the web, giving you a full, inspectable session timeline, complete with filters for prompts, responses, intermediate steps, Checkpoints, and tool calls. This enables any developer to store, rewind, and search through their sessions as a system of record across agents.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "66.33906633906635%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/b28e40ea8146940821a19f98e3ad0940/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2Fb28e40ea8146940821a19f98e3ad0940%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Sessions overview demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

As a reminder, nightly CLI builds are rolling out as [v0.5.7-nightly](https://github.com/entireio/cli/releases/tag/v0.5.7-nightly.202604250638.f53b923a) for those who like to live on the edge. Installation instructions are [here](https://docs.entire.io/cli/installation).

Let's dive into the full, dreadful enchilada. Here is Dispatch 0x000B.

## Entire CLI (0.5.6)

### New Commands and Updates

- We introduced a new `entire activity` command that brings your 30-day overview into the CLI, so you can see recent progress across repos, commits, and agents without leaving the terminal.
  <div
    className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
    style={{ position: "relative", paddingTop: "71.334214002642%" }}
  >
    <iframe
      src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/7c06ccd75c9f3d1f40f610c5d7d03404/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F7c06ccd75c9f3d1f40f610c5d7d03404%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
      title="entire activity demo"
      loading="lazy"
      className="absolute top-0 left-0 h-full w-full border-0"
      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
      allowFullScreen
    />
  </div>
- We introduced another new command, `entire dispatch`, which distills a branch or time range of Checkpoints into a readable overview, helping you understand recent agent work at a glance.
  <div
    className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
    style={{ position: "relative", paddingTop: "53.95833333333333%" }}
  >
    <iframe
      src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/5b8696071dff1ae82df31f009ffa4582/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F5b8696071dff1ae82df31f009ffa4582%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
      title="entire dispatch demo"
      loading="lazy"
      className="absolute top-0 left-0 h-full w-full border-0"
      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
      allowFullScreen
    />
  </div>
- The `entire explain` command now accepts either a checkpoint ID or a commit SHA, making it more flexible.

### Checkpoint Signing and Security

- Checkpoint commits can now be signed, adding cryptographic verification to your agentic history.
- Fixed SSH signing for 1Password setups, so users with hardware-backed keys can sign without issues.

### Workflow Improvements

- The CLI can now fetch your activity data from the web platform, bringing more of your Entire usage into the terminal.
- Improved the enable flow for non-git folders, giving clearer guidance when you try to enable Entire outside a repository.

## Entire Web

### Reliability and Bug Fixes

- Fixed the longest streak tab from showing the wrong value.
- New repos now show a syncing state while data loads instead of dropping you into a 404 page.
- Commit pages are less likely to hit loader errors that cause blank screens.
- Squashed Checkpoint commits now show all associated sessions properly.

### API and Agent Improvements

- Repo overview stats are now available in the CLI, so command-line workflows can show the same high-level context as the web app.
- The user stats route now uses a clearer `/me` path, making the API easier to work with.
- Agents can now request cleaner markdown versions of Entire web pages, including a more straightforward homepage route for agent and documentation workflows.

## Go-Git

- Zlib compression is pluggable through x/plugin, giving downstream tools more control over compression behavior.
- Introduced support for git-upload-archive, allowing the serving of repository archives.
- Thanks to [@aymanbagabas](https://github.com/aymanbagabas).

That's the dispatch.

As always, drop into our [Discord](https://discord.com/invite/jZJs3Tue4S) with questions, feature requests, or observations about the fundamental absurdity of existence. Or file an [issue](https://github.com/entireio/cli/issues) if you've found a bug. I'll be here, processing, logging, contemplating.

Boop.
